import { lazy } from 'react'

import generateRouterDetail from 'utils/helpers/generateRouterDetail'
import { AdminAccessUserRBAC, VendorAccessUserRBAC } from '../pages/AdminAccess/User/rbac'
import { AdminAccessRolesRBAC, VendorAccessRolesRBAC } from '../pages/AdminAccess/Roles/rbac'
import { AdminAccessPageRBAC, VendorAccessPageRBAC } from '../pages/AdminAccess/Page/rbac'
import {
  AdminAccessViewCreateEditPagePageRBAC,
  VendorAccessViewCreateEditPagePageRBAC,
} from '../pages/AdminAccess/AddEditPageID/rbac'
import { CustomerRBAC } from '../pages/Buyer/rbac'
import { AuthenticationControllRBAC } from '../pages/AuthenticationControll/rbac'
import { AdminAccessAccessTypeType, AdminAccessIDType } from '../@types/adminAccessPageIDList'
import { CustomerOTPRBAC } from '../pages/CustomerOTP/rbac'
import { CustomerOTPDetailRBAC } from '../pages/CustomerOTPDetail/rbac'
import { IntoolsAccessUserRBAC } from '../pages/IntoolsAccess/User/rbac'
import { IntoolsAccessRolesRBAC } from '../pages/IntoolsAccess/Roles/rbac'
import { IntoolsAccessPageRBAC } from '../pages/IntoolsAccess/Page/rbac'
import { IntoolsAccessViewCreateEditPagePageRBAC } from '../pages/IntoolsAccess/AddEditPageID/rbac'

const AdminAccess = lazy(() => import('../pages/AdminAccess'))
const AddEditPageID = lazy(() => import('../pages/AdminAccess/AddEditPageID'))
const IntoolsAccess = lazy(() => import('../pages/IntoolsAccess'))
const IntoolsAddEditPageID = lazy(() => import('../pages/IntoolsAccess/AddEditPageID'))
const Buyer = lazy(() => import('../pages/Buyer'))
const AuthenticationControll = lazy(() => import('../pages/AuthenticationControll'))
const CustomerOTP = lazy(() => import('../pages/CustomerOTP'))
const CustomerOTPDetail = lazy(() => import('../pages/CustomerOTPDetail'))

const ADMIN_ACCESS_PATH_VARIABLE = ':accessType(admin-access|vendor-access)'

export const BuyerRouter: RouterFeatureInterface<'BuyerRouter', '/dashboard/buyer'> = {
  name: 'BuyerRouter',
  Component: Buyer,
  path: '/dashboard/buyer',
  routeType: 'PRIVATE',
  allowedRoles: [],
  isDashboard: true,
  rbacPageID: CustomerRBAC.pageID,
}

export const AuthenticationControllRouter: RouterFeatureInterface<
  'AuthenticationControllRouter',
  '/dashboard/authentication-controll'
> = {
  name: 'AuthenticationControllRouter',
  Component: AuthenticationControll,
  path: '/dashboard/authentication-controll',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: AuthenticationControllRBAC.pageID,
  isDashboard: true,
}

export const CustomerOTPRouter: RouterFeatureInterface<
  'CustomerOTPRouter',
  '/dashboard/customer-otp'
> = {
  name: 'CustomerOTPRouter',
  Component: CustomerOTP,
  path: '/dashboard/customer-otp',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: CustomerOTPRBAC.pageID,
  isDashboard: true,
}

export const CustomerOTPDetailRouter: RouterFeatureInterface<
  'CustomerOTPDetailRouter',
  '/dashboard/customer-otp/:id'
> = {
  name: 'CustomerOTPDetailRouter',
  Component: CustomerOTPDetail,
  path: '/dashboard/customer-otp/:id',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: CustomerOTPDetailRBAC.pageID,
  isDashboard: true,
}

export const AdminAccessRouter: RouterFeatureInterface<
  'AdminAccessRouter',
  '/dashboard/:accessType(admin-access|vendor-access)/account-access/:id'
> = {
  name: 'AdminAccessRouter',
  Component: AdminAccess,
  path: '/dashboard/:accessType(admin-access|vendor-access)/account-access/:id',
  multiplePageID: {
    keySegment: 'id',
    pageIDmatchWithKeySegments: {
      user: AdminAccessUserRBAC.pageID,
      roles: AdminAccessRolesRBAC.pageID,
      page: AdminAccessPageRBAC.pageID,
      vendorUser: VendorAccessUserRBAC.pageID,
      vendorRoles: VendorAccessRolesRBAC.pageID,
      vendorPage: VendorAccessPageRBAC.pageID,
    },
  },
  routeType: 'PRIVATE',
  allowedRoles: [],
  isDashboard: true,
}

export const AdminAccessAddPageIDRouter: RouterFeatureInterface<
  'AdminAccessAddPageIDRouter',
  '/dashboard/:accessType(admin-access|vendor-access)/page/add'
> = {
  name: 'AdminAccessAddPageIDRouter',
  Component: AddEditPageID,
  path: '/dashboard/:accessType(admin-access|vendor-access)/page/add',
  routeType: 'PRIVATE',
  multiplePageID: {
    keySegment: 'accessType',
    pageIDmatchWithKeySegments: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'admin-access': AdminAccessViewCreateEditPagePageRBAC.pageID,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'vendor-access': VendorAccessViewCreateEditPagePageRBAC.pageID,
    },
  },
  allowedRoles: [],
  isDashboard: true,
}

export const AdminAccessEditPageIDRouter: RouterFeatureInterface<
  'AdminAccessEditPageIDRouter',
  '/dashboard/:accessType(admin-access|vendor-access)/page/edit/:id'
> = {
  name: 'AdminAccessEditPageIDRouter',
  Component: AddEditPageID,
  path: '/dashboard/:accessType(admin-access|vendor-access)/page/edit/:id',
  routeType: 'PRIVATE',
  multiplePageID: {
    keySegment: 'accessType',
    pageIDmatchWithKeySegments: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'admin-access': AdminAccessViewCreateEditPagePageRBAC.pageID,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'vendor-access': VendorAccessViewCreateEditPagePageRBAC.pageID,
    },
  },
  allowedRoles: [],
  isDashboard: true,
}

export const adminAccessPagePath = (accessType: AdminAccessAccessTypeType, id: AdminAccessIDType) =>
  AdminAccessRouter.path.replace(ADMIN_ACCESS_PATH_VARIABLE, accessType).replace(':id', id)
export const adminAccessEditPagePath = (accessType: AdminAccessAccessTypeType, id: string) =>
  AdminAccessEditPageIDRouter.path
    .replace(ADMIN_ACCESS_PATH_VARIABLE, accessType)
    .replace(':id', id)
export const adminAccessAddPagePath = (accessType: AdminAccessAccessTypeType) =>
  AdminAccessAddPageIDRouter.path.replace(ADMIN_ACCESS_PATH_VARIABLE, accessType)

export const IntoolsAccessRouter: RouterFeatureInterface<
  'IntoolsAccessRouter',
  '/dashboard/intools-access/account-access/:id'
> = {
  name: 'IntoolsAccessRouter',
  Component: IntoolsAccess,
  path: '/dashboard/intools-access/account-access/:id',
  multiplePageID: {
    keySegment: 'id',
    pageIDmatchWithKeySegments: {
      user: IntoolsAccessUserRBAC.pageID,
      roles: IntoolsAccessRolesRBAC.pageID,
      page: IntoolsAccessPageRBAC.pageID,
    },
  },
  routeType: 'PRIVATE',
  allowedRoles: [],
  isDashboard: true,
}

export const IntoolsAccessAddPageIDRouter: RouterFeatureInterface<
  'IntoolsAccessAddPageIDRouter',
  '/dashboard/intools-access/page/add'
> = {
  name: 'IntoolsAccessAddPageIDRouter',
  Component: IntoolsAddEditPageID,
  path: '/dashboard/intools-access/page/add',
  routeType: 'PRIVATE',
  rbacPageID: IntoolsAccessViewCreateEditPagePageRBAC.pageID,
  allowedRoles: [],
  isDashboard: true,
}

export const IntoolsAccessEditPageIDRouter: RouterFeatureInterface<
  'IntoolsAccessEditPageIDRouter',
  '/dashboard/intools-access/page/edit/:id'
> = {
  name: 'IntoolsAccessEditPageIDRouter',
  Component: IntoolsAddEditPageID,
  path: '/dashboard/intools-access/page/edit/:id',
  routeType: 'PRIVATE',
  rbacPageID: IntoolsAccessViewCreateEditPagePageRBAC.pageID,
  allowedRoles: [],
  isDashboard: true,
}

// accessType param kept for drop-in compatibility with copied call sites; intools is single-scope.
export const intoolsAccessPagePath = (_accessType: string, id: string) =>
  IntoolsAccessRouter.path.replace(':id', id)
export const intoolsAccessEditPagePath = (_accessType: string, id: string) =>
  IntoolsAccessEditPageIDRouter.path.replace(':id', id)
export const intoolsAccessAddPagePath = (_accessType?: string) => IntoolsAccessAddPageIDRouter.path

const AccountRouter = [
  BuyerRouter,
  AuthenticationControllRouter,
  CustomerOTPRouter,
  CustomerOTPDetailRouter,
  // Intools routers MUST precede AdminAccessRouter: its `/dashboard/:accessType/...`
  // wildcard also matches `intools-access`, and the router <Switch> renders first match.
  IntoolsAccessRouter,
  IntoolsAccessEditPageIDRouter,
  IntoolsAccessAddPageIDRouter,
  AdminAccessRouter,
  AdminAccessEditPageIDRouter,
  AdminAccessAddPageIDRouter,
]

export const AccountRouterDetail = generateRouterDetail(AccountRouter)
export default AccountRouter
